import { useUser } from '@plato/app-core/hooks/use-user';
import { useLocalStorage } from '@plato/ui-legacy/lib/hooks/use-local-storage';
import { useMemoDeepEqualValue } from '@plato/ui-legacy/lib/hooks/use-memo-deep-equal';
import type { SetValue } from '@plato/ui-legacy/lib/hooks/use-query-params';
import { useQueryParams } from '@plato/ui-legacy/lib/hooks/use-query-params';
import {
    generateQueryParamsString,
    getTransientQueryParamKeys,
    omitQueryParams,
    parseQueryParamsString,
    type QueryParamsObjectConfig,
} from '@plato/ui-legacy/lib/query-params';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

/**
 * Like `useQueryParams`, but persists filter values to localStorage.
 * When the page is visited without URL query params the last-saved
 * filters are restored automatically.
 *
 * Params the config marks with `transientParam` are kept out of localStorage.
 *
 * The storage key is automatically prefixed with the current user's ID
 * so each user gets their own persisted filters.
 *
 * Priority: URL params > localStorage > defaultValues
 */

export function usePersistedQueryParams<TValue extends Record<string, unknown>>(
    config: QueryParamsObjectConfig<TValue>,
    defaultValues: TValue,
    storageKey: string,
): [TValue, SetValue<TValue>] {
    // Derived on every render, so hold a stable reference for the deps below.
    const transientKeys = useMemoDeepEqualValue(getTransientQueryParamKeys(config));

    const user = useUser();
    const fullStorageKey = `filters:${user.id}:${storageKey}`;
    const [storedQuery, setStoredQuery] = useLocalStorage<string | undefined>(fullStorageKey, undefined);

    const fallbackValue = useMemo(() => {
        if (storedQuery) {
            const persisted = new URLSearchParams(omitQueryParams(storedQuery, transientKeys));

            return parseQueryParamsString(persisted, config, defaultValues);
        }

        return defaultValues;
    }, [defaultValues, storedQuery, config, transientKeys]);

    const [queryValue, setQueryValue] = useQueryParams(config, fallbackValue);

    // A functional update has to resolve against the value at the time it runs, not the
    // one this callback closed over: an update queued from an effect that fires after
    // another write would otherwise persist the pre-write state.
    const queryValueRef = useRef(queryValue);

    useLayoutEffect(() => {
        queryValueRef.current = queryValue;
    }, [queryValue]);

    const setValue: SetValue<TValue> = useCallback(
        (newValue, action) => {
            setQueryValue(newValue, action);

            const resolved = typeof newValue === 'function' ? newValue(queryValueRef.current) : newValue;
            queryValueRef.current = { ...queryValueRef.current, ...resolved };
            setStoredQuery(omitQueryParams(generateQueryParamsString(config, resolved), transientKeys));
        },
        [setQueryValue, config, setStoredQuery, transientKeys],
    );

    return [queryValue, setValue];
}
